// Google Tag Manager configuration and helper functions

/**
 * Initialize Google Tag Manager
 * @param {string} gtmId - The Google Tag Manager container ID
 */
export const initTagManager = (gtmId) => {
  if (!gtmId) {
    console.warn('Google Tag Manager ID is required');
    return;
  }

  // Create dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  // Push initial event to dataLayer
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  // Log initialization in development
  if (process.env.NODE_ENV !== 'production') {
    console.log(`Google Tag Manager initialized with ID: ${gtmId}`);
  }
};

/**
 * Push data to the dataLayer
 * @param {Object} data - The data to push to the dataLayer
 */
export const pushToDataLayer = (data) => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
    
    // Log dataLayer push in development
    if (process.env.NODE_ENV !== 'production') {
      console.log('DataLayer push:', data);
    }
  }
};

/**
 * Track user engagement events
 * @param {string} eventName - Name of the event
 * @param {Object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  pushToDataLayer({
    event: eventName,
    ...eventParams
  });
};

/**
 * Track form submissions
 * @param {string} formName - Name of the form
 * @param {string} formStatus - Status of the form submission (success, error)
 */
export const trackFormSubmission = (formName, formStatus) => {
  trackEvent('form_submission', {
    form_name: formName,
    form_status: formStatus
  });
};

/**
 * Track user interactions with elements
 * @param {string} elementCategory - Category of the element (button, link, etc.)
 * @param {string} elementAction - Action performed (click, hover, etc.)
 * @param {string} elementLabel - Label for the element
 */
export const trackInteraction = (elementCategory, elementAction, elementLabel) => {
  trackEvent('user_interaction', {
    element_category: elementCategory,
    element_action: elementAction,
    element_label: elementLabel
  });
};

/**
 * Track scroll depth
 * @param {number} scrollDepth - Scroll depth percentage
 * @param {string} pageSection - Section of the page that was scrolled to
 */
export const trackScrollDepth = (scrollDepth, pageSection) => {
  trackEvent('scroll_depth', {
    scroll_depth: scrollDepth,
    page_section: pageSection,
    page_path: window.location.pathname,
    page_title: document.title
  });
};

/**
 * Track page load performance metrics
 * @param {Object} performanceMetrics - Performance metrics object
 */
export const trackPerformance = (performanceMetrics) => {
  trackEvent('page_performance', performanceMetrics);
};

/**
 * Track ecommerce events (for future implementation)
 * @param {string} eventType - Type of ecommerce event
 * @param {Object} products - Products involved in the event
 * @param {Object} additionalData - Additional data for the event
 */
export const trackEcommerce = (eventType, products, additionalData = {}) => {
  trackEvent(`ecommerce_${eventType}`, {
    products,
    ...additionalData
  });
};

/**
 * Track page view
 * @param {string} pagePath - Path of the page
 * @param {string} pageTitle - Title of the page
 */
export const trackPageView = (pagePath, pageTitle = document.title) => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    page_location: window.location.href
  });
};

/**
 * Track page exit events
 * @param {string} exitType - Type of exit (bounce, exit, navigation)
 * @param {number} timeOnPage - Time spent on page in seconds
 * @param {string} exitTarget - Where the user went (if applicable)
 */
export const trackPageExit = (exitType, timeOnPage, exitTarget = null) => {
  trackEvent('page_exit', {
    exit_type: exitType,
    time_on_page: timeOnPage,
    exit_target: exitTarget,
    page_path: window.location.pathname,
    page_title: document.title,
    exit_timestamp: new Date().toISOString()
  });
};

/**
 * Track user inactivity
 * @param {number} inactiveTime - Time of inactivity in seconds
 */
export const trackInactivity = (inactiveTime) => {
  trackEvent('user_inactivity', {
    inactive_time: inactiveTime,
    page_path: window.location.pathname,
    page_title: document.title
  });
};